<template>
  <section class="bg-[radial-gradient(circle_at_top_left,#fff9f1_0%,#f8f1ea_36%,#f3ebe3_100%)] py-3 sm:py-4">
    <div class="mx-auto max-w-[1480px] px-3 sm:px-5 lg:px-6">
      <div v-if="loading" class="rounded-[24px] border border-dashed border-[#eadfd4] bg-white/90 p-5 text-sm text-muted shadow-sm">
        Loading venue preview...
      </div>

      <div v-else class="space-y-4">
        <nav class="flex flex-wrap items-center gap-2 text-xs text-[#7d6653] sm:text-sm">
          <button type="button" class="inline-flex items-center gap-2 rounded-full border border-[#e4d6ca] bg-white px-3 py-1.5 font-semibold text-ink shadow-sm transition hover:border-brand hover:text-brand" @click="goBack">
            <i class="ri-arrow-left-line"></i>
            Back
          </button>
          <span class="text-[#ccb8a8]">/</span>
          <span class="font-medium">Business</span>
          <span class="text-[#ccb8a8]">/</span>
          <span class="font-medium">{{ businessName }}</span>
          <span class="text-[#ccb8a8]">/</span>
          <span class="min-w-0 truncate font-semibold text-ink">{{ venue.title || 'Venue detail' }}</span>
        </nav>

        <div v-if="error" class="rounded-[18px] border border-amber-200 bg-amber-50 px-4 py-3 text-sm font-medium text-amber-800">
          {{ error }}
        </div>

        <section class="overflow-hidden rounded-[26px] border border-[#eadfd4] bg-white shadow-sm">
          <div class="border-b border-[#efe3d8] px-4 py-3 sm:px-5 lg:px-6">
            <div class="flex flex-wrap items-center justify-between gap-3">
              <div>
                <h1 class="font-serif text-[1.75rem] font-semibold leading-tight text-ink lg:text-[2.25rem]">{{ venue.title || 'Venue detail' }}</h1>
                <p class="mt-2 text-[0.98rem] italic text-[#927660]">{{ venue.tagLine || 'Venue preview for the public detail page.' }}</p>
              </div>
              <span class="rounded-full bg-[#fbf1e6] px-3 py-1.5 text-sm font-semibold text-brand">
                {{ venue.type || 'Venue' }}
              </span>
            </div>
          </div>

          <div class="space-y-3 px-4 py-4 sm:px-5 lg:px-6">
            <div class="mx-auto grid max-w-[980px] grid-cols-2 gap-2.5 sm:gap-3 lg:grid-cols-4 lg:grid-rows-2">
              <button
                type="button"
                class="relative aspect-[4/3] overflow-hidden rounded-[20px] text-left lg:col-span-2 lg:row-span-2 lg:h-[340px] lg:min-h-0 lg:rounded-[24px]"
                @click="openVenueGalleryById(previewGalleryTiles[0])"
              >
                <img
                  v-if="previewGalleryTiles[0]"
                  :src="previewGalleryTiles[0].src"
                  :alt="venue.title"
                  class="h-full w-full rounded-[20px] bg-[#f5efe8] object-cover lg:rounded-[24px]"
                  :class="{ 'object-contain p-4': previewGalleryTiles[0].isPlaceholder }"
                >
                <span v-if="previewHeroTileIsCover" class="absolute left-3 top-3 rounded-full bg-white/95 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wide text-brand shadow-sm">
                  Cover image
                </span>
              </button>

              <button
                v-for="(image, index) in previewGalleryTiles.slice(1, 4)"
                :key="`${image.id || 'hero-side'}-${index}`"
                type="button"
                class="relative aspect-[4/3] overflow-hidden rounded-[20px] text-left lg:h-[164px] lg:min-h-0"
                @click="openVenueGalleryById(image)"
              >
                <img
                  :src="image.src"
                  :alt="`${venue.title} ${index + 2}`"
                  class="h-full w-full rounded-[20px] bg-[#f5efe8] object-cover"
                  :class="{ 'object-contain p-3': image.isPlaceholder }"
                >
                <div
                  v-if="image.overlayText"
                  class="absolute inset-0 flex flex-col items-center justify-center bg-[#1f1208]/55 text-center text-white"
                >
                  <span class="text-[1.45rem] font-semibold leading-none">{{ image.overlayText }}</span>
                  <span class="mt-1.5 text-[10px] font-semibold uppercase tracking-[0.18em]">more photos</span>
                </div>
              </button>
            </div>

            <div class="rounded-[22px] border border-[#eadfd4] bg-[#fffdf9] p-4">
              <div class="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-[#6f5948]">
                <span class="inline-flex items-center gap-2">
                  <i class="ri-map-pin-2-fill text-[#d16c57]"></i>
                  {{ venue.address || 'Location unavailable' }}
                </span>
                <span class="text-[#d8c8bb]">·</span>
                <span>{{ establishedLabel }}</span>
                <span v-if="venue.phone" class="text-[#d8c8bb]">·</span>
                <span v-if="venue.phone" class="inline-flex items-center gap-2">
                  <i class="ri-phone-line text-brand"></i>
                  {{ venue.phoneCode ? `${venue.phoneCode} ` : '' }}{{ venue.phone }}
                </span>
              </div>

              <p class="mt-3 text-[1rem] leading-7 text-[#2f241b]">
                {{ venue.about || venueStory }}
              </p>

              <div v-if="venueHighlightsList.length" class="mt-4">
                <p class="text-xs font-semibold uppercase tracking-[0.18em] text-[#b78969]">Highlights</p>
                <div class="mt-3 flex flex-wrap gap-2">
                  <span v-for="item in venueHighlightsList" :key="item" class="rounded-full bg-[#f5eee8] px-3 py-1.5 text-sm font-semibold text-[#6d5542]">
                    {{ item }}
                  </span>
                </div>
              </div>

              <div v-if="inHouseServicesList.length" class="mt-4">
                <p class="text-xs font-semibold uppercase tracking-[0.18em] text-[#b78969]">In-house services</p>
                <div class="mt-3 grid gap-2 md:grid-cols-2">
                  <div v-for="item in inHouseServicesList" :key="item" class="flex items-center gap-3 text-[1rem] text-[#2f241b]">
                    <span class="text-[#2e8a5d]">✓</span>
                    <span>{{ item }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <div class="grid gap-4 xl:grid-cols-[minmax(0,1fr)_340px]">
          <div class="space-y-4">
            <section v-if="spaces?.length" class="rounded-[24px] border border-[#eadfd4] bg-white p-4 shadow-sm sm:p-5">
              <div>
                <p class="text-xs font-semibold uppercase tracking-[0.18em] text-[#b78969]">Spaces</p>
                <h2 class="mt-2 font-serif text-[1.9rem] font-semibold text-ink">Venue spaces</h2>
              </div>

              <div v-if="spaces?.length" class="mt-4">
                <div class="flex flex-wrap gap-2">
                  <button
                    v-for="item in spaces"
                    :key="item.id"
                    type="button"
                    class="rounded-full border px-4 py-2 text-sm font-semibold transition"
                    :class="selectedSpaceId === item.id ? 'border-[#d9a441] bg-[#fff7e8] text-[#80550d] shadow-sm' : 'border-[#eadfd4] bg-white text-[#6f5948] hover:border-[#d9a441] hover:text-[#80550d]'"
                    @click="selectedSpaceId = item.id"
                  >
                    {{ item.spaceIcon }} {{ item.name }}
                  </button>
                </div>

                <article v-if="selectedSpace" class="mt-4 overflow-hidden rounded-[22px] border border-[#eadfd4] bg-[#fffdfa]">
                  <div class="flex flex-col gap-4 border-b border-[#eadfd4] px-4 py-4 lg:flex-row lg:items-start lg:justify-between">
                    <div class="min-w-0">
                      <h3 class="font-serif text-[1.65rem] font-semibold text-[#1f1208]">{{ selectedSpace.name }}</h3>
                      <div class="mt-3 flex flex-wrap gap-2">
                        <span class="rounded-full bg-[#f6efe8] px-3 py-1.5 text-sm font-semibold text-[#7a624e]">{{ selectedSpace.settingIcon }} {{ selectedSpace.setting || 'Indoor' }}</span>
                        <span class="rounded-full bg-[#f6efe8] px-3 py-1.5 text-sm font-semibold text-[#7a624e]">{{ selectedSpace.spaceTypeIcon }} {{ selectedSpace.spaceType || 'Space' }}</span>
                        <span v-if="selectedSpace.areaSqft" class="rounded-full bg-[#f6efe8] px-3 py-1.5 text-sm font-semibold text-[#7a624e]">📐 {{ selectedSpace.areaSqft }} sqft</span>
                      </div>
                    </div>

                    <div class="text-left lg:text-right">
                      <p class="text-[1.65rem] font-semibold leading-none text-[#1f1208]">{{ selectedSpace.displayMinPrice }}</p>
                      <p v-if="selectedSpace.displayMaxPrice" class="mt-2 text-base text-[#8b715e]">to {{ selectedSpace.displayMaxPrice }} per event</p>
                    </div>
                  </div>

                  <div class="px-4 py-4">
                    <p class="text-[1rem] leading-7 text-[#2f241b]">
                      {{ selectedSpace.description || 'No description added.' }}
                    </p>

                    <div v-if="selectedSpace.seatingItems?.length" class="mt-5">
                      <p class="text-xs font-semibold uppercase tracking-[0.18em] text-[#b78969]">Seating configurations</p>
                      <div class="mt-3 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
                        <article v-for="item in selectedSpace.seatingItems || []" :key="`${selectedSpace.id}-${item.label}`" class="rounded-[16px] border border-[#eadfd4] bg-white px-3 py-3">
                          <div class="flex items-end justify-between gap-3">
                            <p class="text-[1rem] font-medium leading-6 text-[#2f241b]">{{ item.label }}</p>
                            <p class="text-lg font-semibold text-[#1f1208]">{{ item.capacity }}</p>
                          </div>
                        </article>
                      </div>
                    </div>

                    <div class="mt-5 grid gap-5 lg:grid-cols-2">
                      <div>
                        <p class="text-xs font-semibold uppercase tracking-[0.18em] text-[#b78969]">Included amenities</p>
                        <div class="mt-3 space-y-2">
                          <div v-for="item in selectedSpace.includedAmenities || []" :key="`${selectedSpace.id}-amenity-${item}`" class="flex items-start justify-between gap-3 text-[1rem] text-[#2f241b]">
                            <span class="inline-flex items-center gap-3">
                              <span class="text-[#2e8a5d]">✓</span>
                              <span>{{ item }}</span>
                            </span>
                          </div>
                          <p v-if="!selectedSpace.includedAmenities?.length" class="text-sm text-muted">No included amenities added yet.</p>
                        </div>
                      </div>

                      <div>
                        <p class="text-xs font-semibold uppercase tracking-[0.18em] text-[#b78969]">Equipment</p>
                        <div class="mt-3 space-y-2">
                          <div v-for="item in selectedSpace.equipmentItems || []" :key="`${selectedSpace.id}-equipment-${item.label}`" class="flex items-start justify-between gap-3 text-[1rem] text-[#2f241b]">
                            <span class="inline-flex items-center gap-3">
                              <span :class="item.included ? 'text-[#2e8a5d]' : 'text-[#c58a27]'">{{ item.included ? '✓' : '+' }}</span>
                              <span>{{ item.label }}</span>
                            </span>
                            <span :class="item.included ? 'text-[#2e8a5d]' : 'text-[#c58a27]'" class="text-sm font-semibold">
                              {{ item.included ? 'Included' : 'Optional' }}
                            </span>
                          </div>
                          <p v-if="!selectedSpace.equipmentItems?.length" class="text-sm text-muted">No equipment details added yet.</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </article>
              </div>

            </section>

          <section v-if="packages?.length" class="rounded-[24px] border border-[#eadfd4] bg-white p-4 shadow-sm sm:p-5">
            <div>
              <p class="text-xs font-semibold uppercase tracking-[0.18em] text-[#b78969]">Packages</p>
              <h2 class="mt-2 font-serif text-[1.9rem] font-semibold text-ink">Venue packages</h2>
            </div>

            <div v-if="packages?.length" class="mt-4 grid gap-3 md:grid-cols-2">
              <article v-for="item in packages" :key="item.id" class="rounded-[20px] border border-[#eadfd4] bg-[#fffaf5] p-4">
                <div class="flex items-start justify-between gap-4">
                  <div>
                    <h3 class="text-lg font-semibold text-ink">{{ item.name }}</h3>
                    <div class="mt-2 flex flex-wrap gap-2 text-xs text-[#7d634e]">
                      <span class="rounded-full bg-white px-3 py-1 font-semibold ring-1 ring-[#eadfd4]">{{ item.priceLabel }}</span>
                      <span v-if="item.maxGuests" class="rounded-full bg-white px-3 py-1 font-semibold ring-1 ring-[#eadfd4]">{{ item.maxGuests }} guests</span>
                      <span v-if="item.validHours" class="rounded-full bg-white px-3 py-1 font-semibold ring-1 ring-[#eadfd4]">{{ item.validHours }} hrs</span>
                    </div>
                  </div>
                </div>

                <p class="mt-4 text-sm leading-7 text-[#4b3b2f]">{{ item.description || 'No description added.' }}</p>

                <div v-if="item.includedServices?.length" class="mt-4 flex flex-wrap gap-2">
                  <span v-for="service in item.includedServices || []" :key="service" class="rounded-full border border-[#eadfd4] bg-white px-3 py-1 text-xs font-semibold text-[#6f5948]">
                    {{ service }}
                  </span>
                </div>
              </article>
            </div>
          </section>

          <section class="rounded-[24px] border border-[#eadfd4] bg-white p-4 shadow-sm sm:p-5">
            <div>
              <p class="text-xs font-semibold uppercase tracking-[0.18em] text-[#b78969]">Transportation</p>
              <h2 class="mt-2 font-serif text-[1.9rem] font-semibold text-ink">Transportation & venue timings</h2>
            </div>

            <div class="mt-4 grid gap-4 lg:grid-cols-2">
              <div>
                <p class="text-xs font-semibold uppercase tracking-[0.18em] text-[#b78969]">Venue facts</p>
                <div class="mt-4 space-y-4">
                  <article class="flex items-start gap-4">
                    <span class="mt-0.5 text-[1.35rem]">🛏️</span>
                    <div>
                      <p class="text-xs font-semibold uppercase tracking-[0.12em] text-[#b78969]">Rooms</p>
                      <p class="mt-1 text-[1.02rem] leading-7 text-[#2f241b]">{{ venue.noOfRooms || 'Room count not added yet.' }}</p>
                    </div>
                  </article>
                </div>
              </div>

              <div>
                <p class="text-xs font-semibold uppercase tracking-[0.18em] text-[#b78969]">Venue timing</p>
                <div class="mt-4 overflow-hidden rounded-[16px] border border-[#eadfd4]">
                  <div class="grid grid-cols-[28px_minmax(0,1fr)_56px_56px] gap-2 border-b border-[#eadfd4] bg-[#fbf6f0] px-3 py-2.5 text-[10px] font-semibold uppercase tracking-[0.08em] text-[#b78969] sm:grid-cols-[44px_minmax(0,1fr)_80px_80px] sm:px-4 sm:py-3 sm:text-xs">
                    <span></span>
                    <span>Timing</span>
                    <span>Start</span>
                    <span>End</span>
                  </div>
                  <div v-for="item in venueTimingRows" :key="item.key" class="grid grid-cols-[28px_minmax(0,1fr)_56px_56px] gap-2 border-b border-[#eadfd4] px-3 py-2.5 text-sm text-[#2f241b] last:border-b-0 sm:grid-cols-[44px_minmax(0,1fr)_80px_80px] sm:px-4 sm:py-3 sm:text-[1rem]">
                    <span :class="item.available ? 'border-brand bg-brand text-white' : 'border-[#d8c8b9] bg-white text-transparent'" class="inline-flex h-5 w-5 items-center justify-center rounded border text-xs font-bold">✓</span>
                    <span>{{ item.label }}</span>
                    <span>{{ item.start || '-' }}</span>
                    <span>{{ item.end || '-' }}</span>
                  </div>
                </div>
              </div>
            </div>

            <div v-if="hasTransportationDetails" class="mt-4">
              <p class="text-xs font-semibold uppercase tracking-[0.18em] text-[#b78969]">Transportation</p>
              <div v-if="nearbyAreasList.length" class="mt-3 overflow-hidden rounded-[16px] border border-[#eadfd4]">
                <div v-for="item in nearbyAreasList" :key="item" class="flex items-center gap-3 border-b border-[#eadfd4] px-4 py-3 text-[1rem] text-[#2f241b] last:border-b-0">
                  <i class="ri-map-pin-2-fill text-[#d16c57]"></i>
                  <span>{{ item }}</span>
                </div>
              </div>
              <a
                v-if="transportationMapLink"
                :href="transportationMapLink"
                target="_blank"
                rel="noreferrer"
                class="mt-4 inline-flex items-center gap-2 rounded-full border border-[#eadfd4] bg-[#fffaf5] px-4 py-2 text-sm font-semibold text-brand transition hover:border-brand"
              >
                <i class="ri-road-map-line"></i>
                Open saved Google Maps link
              </a>
            </div>
          </section>

          <section class="rounded-[24px] border border-[#eadfd4] bg-white p-4 shadow-sm sm:p-5">
            <div>
              <p class="text-xs font-semibold uppercase tracking-[0.18em] text-[#b78969]">Parking</p>
              <h2 class="mt-2 font-serif text-[1.9rem] font-semibold text-ink">Parking & venue facilities</h2>
            </div>

            <div v-if="parkingDetailRows.length" class="mt-4">
              <p class="text-xs font-semibold uppercase tracking-[0.18em] text-[#b78969]">Car & Valet Parking</p>
              <div class="mt-3 overflow-hidden rounded-[16px] border border-[#eadfd4]">
                <div v-for="item in parkingDetailRows" :key="item.label" class="grid gap-3 border-b border-[#eadfd4] px-4 py-3 last:border-b-0 sm:grid-cols-[24px_minmax(0,1fr)_auto]">
                  <span :class="item.available ? 'text-[#2e8a5d]' : 'text-[#d64545]'" class="text-[1.2rem] leading-none">
                    <i :class="item.iconClass"></i>
                  </span>
                  <span class="text-[1rem] text-[#2f241b]">{{ item.label }}</span>
                  <span class="font-semibold text-ink">{{ item.value }}</span>
                </div>
              </div>
            </div>

            <div v-if="additionalFacilitiesList.length" class="mt-4">
              <p class="text-xs font-semibold uppercase tracking-[0.18em] text-[#b78969]">Additional facilities</p>
              <div class="mt-3 overflow-hidden rounded-[16px] border border-[#eadfd4]">
                <div v-for="item in additionalFacilitiesList" :key="item.label" class="grid gap-3 border-b border-[#eadfd4] px-4 py-3 last:border-b-0 sm:grid-cols-[24px_minmax(0,1fr)]">
                  <span :class="item.available ? 'text-[#2e8a5d]' : 'text-[#d64545]'" class="text-[1.2rem] leading-none">
                    <i :class="item.iconClass"></i>
                  </span>
                  <span class="text-[1rem] text-[#2f241b]">{{ item.label }}</span>
                </div>
              </div>
            </div>
          </section>

          <section class="rounded-[24px] border border-[#eadfd4] bg-white p-4 shadow-sm sm:p-5">
            <div>
              <p class="text-xs font-semibold uppercase tracking-[0.18em] text-[#b78969]">Policies</p>
              <h2 class="mt-2 font-serif text-[1.9rem] font-semibold text-ink">Venue policies</h2>
            </div>

            <div class="mt-4 grid gap-4 lg:grid-cols-2">
              <div>
                <p class="text-xs font-semibold uppercase tracking-[0.18em] text-[#b78969]">Venue rules</p>
                <div class="mt-4 space-y-4">
                  <article v-for="item in policyRuleItems" :key="item.label" class="flex items-start gap-4">
                    <span class="mt-0.5 text-[1.35rem]">{{ item.icon }}</span>
                    <div>
                      <p class="text-xs font-semibold uppercase tracking-[0.12em] text-[#b78969]">{{ item.label }}</p>
                      <div v-if="item.rows?.length" class="mt-2 overflow-hidden rounded-[18px] border border-[#eadfd4]">
                        <div v-for="row in item.rows" :key="row.label" class="grid gap-3 border-b border-[#eadfd4] px-4 py-3 last:border-b-0 sm:grid-cols-[24px_minmax(0,1fr)]">
                          <span :class="row.available ? 'text-[#2e8a5d]' : 'text-[#d64545]'" class="text-[1.2rem] leading-none">
                            <i :class="row.iconClass"></i>
                          </span>
                          <span class="text-[1rem] text-[#2f241b]">{{ row.label }}</span>
                        </div>
                      </div>
                      <p v-else class="mt-1 text-[1.02rem] leading-7 text-[#2f241b]">{{ item.value }}</p>
                    </div>
                  </article>
                </div>
              </div>

              <div>
                <p class="text-xs font-semibold uppercase tracking-[0.18em] text-[#b78969]">Booking terms</p>
                <div class="mt-4 space-y-4">
                  <article v-for="item in bookingTermItems" :key="item.label" class="flex items-start gap-4">
                    <span class="mt-0.5 text-[1.35rem]">{{ item.icon }}</span>
                    <div>
                      <p class="text-xs font-semibold uppercase tracking-[0.12em] text-[#b78969]">{{ item.label }}</p>
                      <p class="mt-1 text-[1.02rem] leading-7 text-[#2f241b]">{{ item.value }}</p>
                    </div>
                  </article>
                </div>
              </div>
            </div>
          </section>

          <section class="rounded-[24px] border border-[#eadfd4] bg-white p-4 shadow-sm sm:p-5">
            <div>
              <p class="text-xs font-semibold uppercase tracking-[0.18em] text-[#b78969]">Catering & Food Restrictions</p>
              <h2 class="mt-2 font-serif text-[1.9rem] font-semibold text-ink">Catering & food restrictions</h2>
              <div v-if="cateringRestrictionRows.length" class="mt-4 overflow-hidden rounded-[16px] border border-[#eadfd4]">
                <div v-for="row in cateringRestrictionRows" :key="row.label" class="grid gap-3 border-b border-[#eadfd4] px-4 py-3 last:border-b-0 sm:grid-cols-[24px_minmax(0,1fr)]">
                  <span :class="row.available ? 'text-[#2e8a5d]' : 'text-[#d64545]'" class="text-[1.2rem] leading-none">
                    <i :class="row.iconClass"></i>
                  </span>
                  <span class="text-[1rem] text-[#2f241b]">{{ row.label }}</span>
                </div>
              </div>
              <p class="mt-4 text-[1rem] leading-7 text-[#2f241b]">
                Below listed prices are <strong>excluding all taxes.</strong>
              </p>
              <p class="text-[1rem] leading-7 text-[#2f241b]">
                Menu prices are subject to change, will be varies depending on guest requirement and number of people. Please contact manager for more details.
              </p>
            </div>

            <div v-if="menuPricingItems.length" class="mt-4 overflow-x-auto rounded-[20px] border border-[#eadfd4]">
              <table class="min-w-[380px] border-collapse bg-white text-left sm:min-w-full">
                <thead class="bg-[#fbf6f0]">
                  <tr>
                    <th class="border-b border-r border-[#eadfd4] px-4 py-3 text-sm font-semibold text-ink">Menu Name</th>
                    <th class="border-b border-[#eadfd4] px-4 py-3 text-sm font-semibold text-ink">{{ menuPricingUnitLabel }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="item in menuPricingItems" :key="item.name">
                    <td class="border-b border-r border-[#eadfd4] px-4 py-3 text-[1rem] text-[#2f241b]">
                      <span class="inline-flex items-center gap-2">
                        <span class="h-3.5 w-3.5 rounded-[4px] bg-white p-[2px]" :class="item.swatchBorderClass">
                          <span class="block h-full w-full rounded-[2px]" :class="item.swatchClass"></span>
                        </span>
                        {{ item.name }}
                      </span>
                    </td>
                    <td class="border-b border-[#eadfd4] px-4 py-3 text-[1rem] font-medium text-[#2f241b]">{{ item.price }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p v-else class="mt-5 rounded-[18px] border border-dashed border-[#eadfd4] bg-[#fffaf5] px-4 py-3 text-sm text-muted">
              Menu pricing not added yet.
            </p>
          </section>

          <section class="rounded-[24px] border border-[#eadfd4] bg-white p-4 shadow-sm sm:p-5">
            <div>
              <p class="text-xs font-semibold uppercase tracking-[0.18em] text-[#b78969]">Location</p>
              <h2 class="mt-2 font-serif text-[1.9rem] font-semibold text-ink">{{ venue.address || 'Venue location' }}</h2>
            </div>

            <div class="mt-4 overflow-hidden rounded-[20px] border border-[#d8e7ef] bg-[#dff0fa]">
              <div
                class="relative flex min-h-[190px] items-center justify-center px-4 py-7 text-center"
                style="background-image: linear-gradient(to right, rgba(130,160,180,0.14) 1px, transparent 1px), linear-gradient(to bottom, rgba(130,160,180,0.14) 1px, transparent 1px); background-size: 112px 100%, 100% 34px;"
              >
                <div class="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(255,255,255,0.22),transparent_58%)]"></div>
                <div class="relative z-10 flex flex-col items-center">
                  <div class="relative">
                    <span class="block h-5 w-5 rounded-full bg-[#ff5a49] shadow-[0_6px_14px_rgba(255,90,73,0.28)]"></span>
                    <span class="absolute left-1/2 top-4 h-8 w-[3px] -translate-x-1/2 rounded-full bg-[#4a89a8]"></span>
                  </div>
                  <p class="mt-10 font-serif text-[1.35rem] font-semibold text-[#2f241b]">{{ venue.address || 'Venue location not added yet.' }}</p>
                  <p class="mt-1 text-[1rem] text-[#5b6c76]">{{ venue.address || 'Hyderabad, Telangana' }}</p>
                  <a
                    href="#"
                    class="mt-5 inline-flex items-center gap-2 rounded-full bg-[#c99a22] px-5 py-3 text-sm font-semibold text-white transition hover:bg-[#b88817]"
                  >
                    Open in Google Maps
                    <i class="ri-arrow-right-up-line"></i>
                  </a>
                </div>
              </div>
            </div>
          </section>

        </div>

        <aside class="self-start xl:sticky xl:top-24">
          <section class="rounded-[24px] border border-[#eadfd4] bg-white p-4 shadow-sm sm:p-5">
            <p class="text-xs font-semibold uppercase tracking-[0.18em] text-[#b78969]">Preview</p>
            <h2 class="mt-2 font-serif text-[1.75rem] font-semibold text-ink">Public page summary</h2>
            <p class="mt-2 text-sm leading-7 text-muted">This preview mirrors the public venue detail layout so you can review what guests will see.</p>

            <div class="mt-5 space-y-4">
              <div class="rounded-[22px] border border-[#eadfd4] bg-[#fffaf5] px-4 py-4">
                <p class="text-xs font-semibold uppercase tracking-wide text-[#b78969]">Business</p>
                <p class="mt-2 text-sm font-semibold text-ink">{{ businessName }}</p>
              </div>
              <div class="rounded-[22px] border border-[#eadfd4] bg-[#fffaf5] px-4 py-4">
                <p class="text-xs font-semibold uppercase tracking-wide text-[#b78969]">Venue status</p>
                <p class="mt-2 text-sm font-semibold text-ink">{{ venue.status || 'Draft' }}</p>
              </div>
              <div class="rounded-[22px] border border-[#eadfd4] bg-[#fffaf5] px-4 py-4">
                <p class="text-xs font-semibold uppercase tracking-wide text-[#b78969]">Preview readiness</p>
                <div class="mt-3 space-y-2 text-sm text-[#4b3b2f]">
                  <div class="flex items-center justify-between gap-3">
                    <span>Gallery images</span>
                    <span class="font-semibold text-ink">{{ gallery.length }}</span>
                  </div>
                  <div class="flex items-center justify-between gap-3">
                    <span>Spaces configured</span>
                    <span class="font-semibold text-ink">{{ spaces.length }}</span>
                  </div>
                  <div class="flex items-center justify-between gap-3">
                    <span>Packages configured</span>
                    <span class="font-semibold text-ink">{{ packages.length }}</span>
                  </div>
                  <div class="flex items-center justify-between gap-3">
                    <span>Partners linked</span>
                    <span class="font-semibold text-ink">{{ partners.length }}</span>
                  </div>
                </div>
              </div>
            </div>

            <button
              type="button"
              class="mt-5 w-full rounded-2xl bg-brand px-4 py-3 text-sm font-semibold text-white transition hover:bg-brandDark"
              @click="openVenueGallery()"
            >
              Open full gallery
            </button>
          </section>
        </aside>
      </div>
    </div>
  </div>
</section>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import lightGallery from 'lightgallery'
import lgThumbnail from 'lightgallery/plugins/thumbnail'
import lgZoom from 'lightgallery/plugins/zoom'
import api from '@/services/vendor/api'

const route = useRoute()
const router = useRouter()

const loading = ref(false)
const error = ref('')
const businessName = ref(String(route.query.businessName || 'Business'))
const venue = ref({
  id: '',
  title: '',
  type: '',
  tagLine: '',
  about: '',
  address: '',
  status: '',
  yearEstablished: '',
  phone: '',
  phoneCode: '',
  email: '',
  website: '',
  highlights: [],
  inHouseServices: [],
  additionalFacilities: [],
  additionalFacilitiesStatuses: [],
  noOfRooms: '',
  parkingAvailable: '',
  valetParking: '',
  parkingVehicleCount: '',
  morningTimingFrom: '',
  morningTimingTo: '',
  eveningTimingFrom: '',
  eveningTimingTo: '',
  sessions: [],
  nearbyAreas: [],
  transportationMapLink: ''
})
const spaces = ref([])
const packages = ref([])
const partners = ref([])
const gallery = ref([])
const galleryLightbox = ref(null)
const selectedSpaceId = ref('')
const noImagesPlaceholderSrc = '/assets/icons/no-images.svg'
const policies = ref({
  inHouseCatering: '',
  outsideCatering: '',
  cateringFoodType: '',
  cateringPriceUnit: '',
  pricingBasisCount: '',
  nonVegPricingBasisCount: '',
  decorPolicy: '',
  musicPolicy: '',
  alcoholInhouse: '',
  alcoholOutside: '',
  minGuests: '',
  maxGuests: '',
  decorTime: '',
  cleanTime: '',
  noiseMusic: '',
  otherHouseRules: '',
  cancelTerms: '',
  depositPercent: ''
})

const previewPlaceholderImages = [
  {
    id: 'placeholder-1',
    src: noImagesPlaceholderSrc,
    tagName: 'No image available',
    isCover: false,
    isPlaceholder: true
  },
  {
    id: 'placeholder-2',
    src: noImagesPlaceholderSrc,
    tagName: 'No image available',
    isCover: false,
    isPlaceholder: true
  },
  {
    id: 'placeholder-3',
    src: noImagesPlaceholderSrc,
    tagName: 'No image available',
    isCover: false,
    isPlaceholder: true
  },
  {
    id: 'placeholder-4',
    src: noImagesPlaceholderSrc,
    tagName: 'No image available',
    isCover: false,
    isPlaceholder: true
  },
  {
    id: 'placeholder-5',
    src: noImagesPlaceholderSrc,
    tagName: 'No image available',
    isCover: false,
    isPlaceholder: true
  }
]

const normalizeApiCollection = (data, keys = []) => {
  const root = data?.data ?? data
  if (Array.isArray(root)) return root
  for (const key of keys) {
    if (Array.isArray(root?.[key])) return root[key]
  }
  return []
}

const splitToArray = (value) =>
  String(value || '')
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean)

const splitSessionValues = (value) => String(value || '').split(',').map((entry) => entry.trim())

const getFirstValue = (...values) => values.find((value) => String(value ?? '').trim() !== '') ?? ''

const normalizeVenueSessionTypeValue = (value) => {
  const normalized = String(value || '').trim().toLowerCase()
  if (!normalized) return ''
  if (normalized.includes('request')) return 'on-request'
  if (normalized.includes('dinner') || normalized.includes('night') || normalized.includes('evening')) return 'evening'
  if (normalized.includes('morning') || normalized.includes('lunch') || normalized.includes('afternoon')) return 'morning'
  return normalized
}

const buildVenueTimingSessions = (item = {}) => {
  const types = splitSessionValues(item?.session_type)
  const fromTimes = splitSessionValues(item?.session_from_time)
  const toTimes = splitSessionValues(item?.session_to_time)
  const total = Math.max(types.length, fromTimes.length, toTimes.length, 0)

  const sessions = Array.from({ length: total }, (_, index) => {
    const type = normalizeVenueSessionTypeValue(types[index])
    return {
      type,
      fromTime: getFirstValue(fromTimes[index], type === 'morning' ? item?.session_from_time_mrng : '', type === 'evening' ? item?.session_from_time_night : ''),
      toTime: getFirstValue(toTimes[index], type === 'morning' ? item?.session_to_time_mrng : '', type === 'evening' ? item?.session_to_time_night : '')
    }
  }).filter((session) => session.type || session.fromTime || session.toTime)

  if ((item?.session_from_time_mrng || item?.session_to_time_mrng) && !sessions.some((session) => session.type === 'morning')) {
    sessions.push({ type: 'morning', fromTime: item.session_from_time_mrng || '', toTime: item.session_to_time_mrng || '' })
  }
  if ((item?.session_from_time_night || item?.session_to_time_night) && !sessions.some((session) => session.type === 'evening')) {
    sessions.push({ type: 'evening', fromTime: item.session_from_time_night || '', toTime: item.session_to_time_night || '' })
  }
  if (isYesValue(item?.session_on_request) && !sessions.some((session) => session.type === 'on-request')) {
    sessions.push({ type: 'on-request', fromTime: '', toTime: '' })
  }

  return sessions
}

const normalizeVenue = (item = {}) => ({
  id: String(item?.id || item?.venue_id || item?.vendor_business_venue_id || route.params.venueId || ''),
  title: item?.title || item?.name || item?.venue_name || 'Untitled venue',
  type: item?.type_name || item?.type || item?.venue_type_name || item?.venue_type || '',
  tagLine: item?.tag_line || item?.tagLine || '',
  about: item?.about || item?.description || '',
  address: item?.fulladdress || item?.address || item?.location || '',
  status: String(item?.status || '').trim() === '1' ? 'Active' : item?.status || 'Draft',
  yearEstablished: item?.est_year || item?.year_established || item?.yearEstablished || '',
  phone: item?.phone || item?.mobile || item?.contact_number || '',
  phoneCode: item?.phone_code || item?.phoneCode || '',
  email: item?.email || '',
  website: item?.website || '',
  highlights: splitToArray(item?.highlights || item?.venue_highlights),
  inHouseServices: splitToArray(item?.in_house_services || item?.inHouseServices),
  additionalFacilities: splitToArray(item?.additional_facilities || item?.additionalFacilities),
  additionalFacilitiesStatuses: splitToArray(item?.additional_facilities_status || item?.additionalFacilitiesStatus),
  noOfRooms: item?.no_of_rooms || item?.rooms_count || '',
  parkingAvailable: getFirstValue(item?.car_parking_available, item?.car_parking_avail, item?.parking_available),
  valetParking: item?.wallet_parking || item?.valet_parking || '',
  parkingVehicleCount: getFirstValue(item?.parking_vehicle_count, item?.no_of_vehicles),
  morningTimingFrom: getFirstValue(item?.venue_timing_morning_from, item?.morning_timing_from, item?.mrng_from_time),
  morningTimingTo: getFirstValue(item?.venue_timing_morning_to, item?.morning_timing_to, item?.mrng_to_time),
  eveningTimingFrom: getFirstValue(item?.venue_timing_evening_from, item?.evening_timing_from, item?.evening_from_time),
  eveningTimingTo: getFirstValue(item?.venue_timing_evening_to, item?.evening_timing_to, item?.evening_to_time),
  sessions: buildVenueTimingSessions(item),
  nearbyAreas: splitToArray(item?.nearby_areas || item?.transportation_nearby_areas),
  transportationMapLink: item?.transportation_map_link || item?.google_maps_link || item?.map_link || ''
})

const normalizeSpace = (item = {}, index = 0) => {
  const configurations = splitToArray(item?.seat_config || item?.seatConfig)
  const capacities = splitToArray(item?.seat_capacity || item?.seatCapacity)
  const amenities = splitToArray(item?.amenities)
  const spaceType = item?.space_type || item?.spaceType || ''
  const setting = item?.setting || 'Indoor'
  const seatingItems = configurations.map((label, configIndex) => ({
    label,
    capacity: capacities[configIndex] || 'N/A'
  }))
  const includedAmenities = amenities.slice(0, 6)
  const equipmentItems = amenities.slice(6).map((label) => ({
    label,
    included: true
  }))
  return {
    id: String(item?.space_id || item?.id || `space-${index}`),
    name: item?.name || 'Untitled space',
    spaceType,
    setting,
    spaceIcon: inferSpaceIcon(spaceType || item?.name || ''),
    spaceTypeIcon: inferSpaceIcon(spaceType || ''),
    settingIcon: inferSpaceIcon(setting),
    areaSqft: item?.area_sqft || item?.areaSqft || '',
    description: item?.description || '',
    minPrice: item?.min_price || item?.minPrice || '',
    maxPrice: item?.max_price || item?.maxPrice || '',
    displayMinPrice: formatCurrency(item?.min_price || item?.minPrice) || 'Price on request',
    displayMaxPrice: formatCurrency(item?.max_price || item?.maxPrice) || '',
    priceLabel: formatPriceRange(item?.min_price || item?.minPrice, item?.max_price || item?.maxPrice),
    amenities,
    seatingItems,
    includedAmenities,
    equipmentItems,
    seatingSummary: configurations.map((label, configIndex) => {
      const capacity = capacities[configIndex]
      return capacity ? `${label}: ${capacity}` : label
    })
  }
}

const normalizePackage = (item = {}, index = 0) => ({
  id: String(item?.package_id || item?.id || `package-${index}`),
  name: item?.name || 'Untitled package',
  description: item?.description || '',
  price: item?.price || '',
  maxGuests: item?.max_guests || item?.maxGuests || '',
  validHours: item?.valid_hours || item?.validHours || '',
  includedServices: splitToArray(item?.included_services || item?.includedServices),
  priceLabel: formatCurrency(item?.price) || 'Price on request'
})

const normalizePartner = (item = {}, index = 0) => ({
  id: String(item?.vendor_partner_id || item?.id || `partner-${index}`),
  name: item?.name || 'Partner',
  category: item?.category || '',
  phone: item?.phone || ''
})

const normalizeVenueGalleryEntries = (source) => {
  const items = Array.isArray(source) ? source : [source]
  return items
    .flatMap((item, itemIndex) => {
      if (!item) return []
      if (typeof item === 'string') {
        return [{
          id: `gallery-${itemIndex}-${item}`,
          src: item,
          tagName: '',
          isCover: false
        }]
      }
      const imageKeys = ['image1', 'image2', 'image3', 'image4', 'image', 'image_url', 'url', 'path', 'img', 'src']
      const entryId = String(item?.id || item?.gallery_id || item?.venue_gallery_id || item?.img_id || '')
      return imageKeys
        .map((key, keyIndex) => {
          const src = item?.[key] || ''
          if (!src) return null
          return {
            id: entryId || `gallery-${itemIndex}-${keyIndex}-${src}`,
            src,
            tagName: String(item?.tag_name || item?.tagName || item?.name || '').trim(),
            isCover: String(item?.is_cover || item?.isCover || '0') === '1'
          }
        })
        .filter(Boolean)
    })
    .filter((entry) => entry?.src)
}

const formatCurrency = (value) => {
  const numeric = Number(String(value || '').replace(/[^\d.]/g, ''))
  return Number.isFinite(numeric) && numeric > 0 ? `Rs. ${numeric.toLocaleString('en-IN')}` : ''
}

const formatPriceRange = (minPrice, maxPrice) => {
  const minValue = formatCurrency(minPrice)
  const maxValue = formatCurrency(maxPrice)
  if (minValue && maxValue) return `${minValue} - ${maxValue}`
  return minValue || maxValue || 'Price on request'
}

const formatYesNo = (value) => {
  const normalized = String(value || '').trim().toLowerCase()
  if (!normalized) return ''
  if (['1', 'yes', 'true'].includes(normalized)) return 'Yes'
  if (['0', 'no', 'false'].includes(normalized)) return 'No'
  return String(value || '').trim()
}

const isYesValue = (value) => formatYesNo(value).toLowerCase() === 'yes'

const getStatusState = (value, fallback = null) => {
  const normalized = String(value ?? '').trim().toLowerCase()
  if (!normalized) return fallback
  if (['0', 'no', 'false', 'off'].includes(normalized) || /not available|not provided|unavailable|not allowed|do not|no\b/.test(normalized)) return false
  if (['1', 'yes', 'true', 'on'].includes(normalized) || /available|allowed|provided|permit|partner|panel|approved|own kitchen|welcome/.test(normalized)) return true
  return fallback
}

const getStatusIconClass = (available) => available ? 'ri-checkbox-circle-fill' : 'ri-close-circle-fill'

const formatStatusPolicyValue = (value, label) => {
  const available = getStatusState(value)
  const text = String(value || '').trim()
  if (available === null && !text) return ''
  return `${label}${text && text !== label ? `: ${text}` : ''}`
}

const formatStatusPolicyRow = (value, label) => {
  const available = getStatusState(value)
  if (available === null) return null
  return {
    label,
    iconClass: getStatusIconClass(available),
    available
  }
}

const parseBothFoodPricing = (value) => {
  const parsed = {
    veg: '',
    nonVeg: ''
  }

  String(value || '').split('||').map((item) => item.trim()).filter(Boolean).forEach((part) => {
    const [rawKey, rawValue] = part.split('::')
    const key = String(rawKey || '').trim().toLowerCase()
    const price = String(rawValue || '').trim()
    if (key === 'veg') parsed.veg = price
    if (key === 'nonveg') parsed.nonVeg = price
  })

  return parsed
}

const inferSpaceIcon = (value) => {
  const normalized = String(value || '').trim().toLowerCase()
  if (!normalized) return '🏛️'
  if (normalized.includes('lawn') || normalized.includes('garden')) return '🌿'
  if (normalized.includes('terrace') || normalized.includes('rooftop')) return '⛱️'
  if (normalized.includes('pool')) return '🏊'
  if (normalized.includes('outdoor')) return '☀️'
  if (normalized.includes('indoor')) return '🏠'
  if (normalized.includes('hall') || normalized.includes('ballroom')) return '🏛️'
  return '🏛️'
}

const venueCity = computed(() => {
  const address = String(venue.value.address || '').trim()
  if (!address) return 'City'
  const segments = address.split(',').map((item) => item.trim()).filter(Boolean)
  return segments.length >= 2 ? segments[segments.length - 2] : segments[0] || 'City'
})

const establishedLabel = computed(() => venue.value.yearEstablished ? `Est. ${venue.value.yearEstablished}` : 'Est. Not set')
const selectedSpace = computed(() => {
  if (!spaces.value.length) return null
  return spaces.value.find((item) => item.id === selectedSpaceId.value) || spaces.value[0]
})

const VISIBLE_GALLERY_TILE_COUNT = 4

const previewGalleryTiles = computed(() => {
  const sortedGallery = [...gallery.value].sort((left, right) => Number(right.isCover) - Number(left.isCover))
  const baseTiles = sortedGallery.slice(0, VISIBLE_GALLERY_TILE_COUNT)
  const missing = Math.max(0, VISIBLE_GALLERY_TILE_COUNT - baseTiles.length)
  const filledTiles = [...baseTiles, ...previewPlaceholderImages.slice(0, missing)].map((item, index) => ({
    ...item,
    id: item.id || `preview-tile-${index}`,
    isPlaceholder: Boolean(item.isPlaceholder),
    overlayText: ''
  }))

  if (!filledTiles.length) {
    return previewPlaceholderImages.map((item, index) => ({
      ...item,
      id: item.id || `preview-tile-${index}`,
      overlayText: ''
    }))
  }

  const hiddenCount = Math.max(0, sortedGallery.length - VISIBLE_GALLERY_TILE_COUNT)
  if (filledTiles[VISIBLE_GALLERY_TILE_COUNT - 1]) {
    filledTiles[VISIBLE_GALLERY_TILE_COUNT - 1] = {
      ...filledTiles[VISIBLE_GALLERY_TILE_COUNT - 1],
      overlayText: hiddenCount ? `+${hiddenCount}` : ''
    }
  }

  return filledTiles
})
const previewHeroTileIsCover = computed(() => Boolean(previewGalleryTiles.value[0] && previewGalleryTiles.value[0].isCover))

const lightboxGalleryImages = computed(() => {
  const source = gallery.value.length
    ? [...gallery.value].sort((left, right) => Number(right.isCover) - Number(left.isCover))
    : previewPlaceholderImages

  return source.map((item, index) => ({
    id: item.id || `lightbox-${index}`,
    src: item.src,
    thumb: item.src,
    tagName: item.tagName || '',
    isCover: Boolean(item.isCover),
    isPlaceholder: Boolean(item.isPlaceholder)
  }))
})

const venueStory = computed(() => (
  venue.value.about
    || `Established to host memorable celebrations in ${venueCity.value}, ${venue.value.title || 'this venue'} brings together flexible spaces, hospitality support, and a layout designed for weddings, parties, and social events.`
))

const venueHighlightsList = computed(() => {
  const items = venue.value.highlights.length
    ? venue.value.highlights
    : ['Award Winning', '5-Star Rated', 'Valet Parking', 'Wheelchair Accessible', 'Power Backup', 'CCTV Surveillance']
  return items.slice(0, 8)
})

const inHouseServicesList = computed(() => {
  const packageServices = packages.value.flatMap((item) => item.includedServices || [])
  const venueServices = Array.isArray(venue.value.inHouseServices) ? venue.value.inHouseServices : []
  const servicePool = [...venueServices, ...packageServices]
    .map((item) => String(item || '').trim())
    .filter(Boolean)

  const uniqueServices = [...new Set(servicePool)]
  return uniqueServices.length
    ? uniqueServices.slice(0, 8)
    : ['Catering', 'DJ / Music', 'Valet Parking', 'Event Coordination', 'Decoration', 'Photography', 'Bar', 'Guest Support']
})
const additionalFacilitiesList = computed(() => {
  const items = Array.isArray(venue.value.additionalFacilities) ? venue.value.additionalFacilities : []
  const statuses = Array.isArray(venue.value.additionalFacilitiesStatuses) ? venue.value.additionalFacilitiesStatuses : []
  return items.map((item, index) => {
    const available = getStatusState(statuses[index], getStatusState(item, true))
    return {
      label: item,
      available,
      iconClass: getStatusIconClass(available)
    }
  }).filter((item) => item.label)
})
const nearbyAreasList = computed(() => {
  const items = Array.isArray(venue.value.nearbyAreas) ? venue.value.nearbyAreas : []
  return items.map((item) => String(item || '').trim()).filter(Boolean)
})
const transportationMapLink = computed(() => String(venue.value.transportationMapLink || '').trim())
const parkingDetailRows = computed(() => {
  const carParking = formatYesNo(venue.value.parkingAvailable)
  const valetParking = formatYesNo(venue.value.valetParking)
  const vehicleCount = String(venue.value.parkingVehicleCount || '').trim()
  const rows = []

  if (carParking) {
    const available = isYesValue(venue.value.parkingAvailable)
    rows.push({ label: 'Car Parking available', value: carParking, available, iconClass: getStatusIconClass(available) })
  }
  if (isYesValue(venue.value.parkingAvailable) && vehicleCount) {
    rows.push({ label: 'Car parking available for vehicle count', value: vehicleCount, available: true, iconClass: getStatusIconClass(true) })
  }
  if (valetParking) {
    const available = isYesValue(venue.value.valetParking)
    rows.push({
      label: available ? 'Valet Parking available' : 'Valet parking not available',
      value: valetParking,
      available,
      iconClass: getStatusIconClass(available)
    })
  }

  return rows
})
const alcoholPolicySummary = computed(() => {
  const items = [
    formatStatusPolicyValue(policies.value.alcoholInhouse, 'In-house bar / alcohol available'),
    formatStatusPolicyValue(policies.value.alcoholOutside, 'Outside alcohol permitted')
  ].filter(Boolean)

  return items.join(' · ') || 'Alcohol policy not added yet.'
})
const venueTimingRows = computed(() => {
  const sessionByType = (type) => (Array.isArray(venue.value.sessions) ? venue.value.sessions : []).find((session) => session.type === type)
  const morningSession = sessionByType('morning')
  const eveningSession = sessionByType('evening')
  const requestSession = sessionByType('on-request')

  return [
    {
      key: 'morning',
      label: 'Morning',
      available: Boolean(morningSession || venue.value.morningTimingFrom || venue.value.morningTimingTo),
      start: morningSession?.fromTime || venue.value.morningTimingFrom || '',
      end: morningSession?.toTime || venue.value.morningTimingTo || ''
    },
    {
      key: 'evening',
      label: 'Evening',
      available: Boolean(eveningSession || venue.value.eveningTimingFrom || venue.value.eveningTimingTo),
      start: eveningSession?.fromTime || venue.value.eveningTimingFrom || '',
      end: eveningSession?.toTime || venue.value.eveningTimingTo || ''
    },
    {
      key: 'on-request',
      label: 'On-Request',
      available: Boolean(requestSession),
      start: requestSession ? 'On request' : '',
      end: requestSession ? 'On request' : ''
    }
  ]
})
const hasTransportationDetails = computed(() => nearbyAreasList.value.length > 0 || Boolean(transportationMapLink.value))
const menuPricingUnitLabel = computed(() => policies.value.cateringPriceUnit || 'Per Person')
const menuPricingItems = computed(() => {
  const foodType = String(policies.value.cateringFoodType || '').trim()
  const pricingCount = String(policies.value.pricingBasisCount || '').trim()
  const nonVegPricingCount = String(policies.value.nonVegPricingBasisCount || '').trim()
  const normalizedFoodType = foodType.toLowerCase()
  const bothPricing = parseBothFoodPricing(pricingCount)
  const hasBoth = normalizedFoodType.includes('both')
  const rows = []

  if (hasBoth) {
    if (bothPricing.veg || pricingCount) rows.push({ name: 'Veg', rawPrice: bothPricing.veg || pricingCount, swatchBorderClass: 'border border-[#1f9d45]', swatchClass: 'bg-[#1f9d45]' })
    if (bothPricing.nonVeg || nonVegPricingCount) rows.push({ name: 'Non Veg', rawPrice: bothPricing.nonVeg || nonVegPricingCount, swatchBorderClass: 'border border-[#d45711]', swatchClass: 'bg-[#d45711]' })
  } else if (normalizedFoodType.includes('non')) {
    rows.push({ name: 'Non Veg', rawPrice: pricingCount, swatchBorderClass: 'border border-[#d45711]', swatchClass: 'bg-[#d45711]' })
  } else if (normalizedFoodType.includes('veg')) {
    rows.push({ name: 'Veg', rawPrice: pricingCount, swatchBorderClass: 'border border-[#1f9d45]', swatchClass: 'bg-[#1f9d45]' })
  } else if (pricingCount) {
    rows.push({ name: 'Menu', rawPrice: pricingCount, swatchBorderClass: 'border border-[#b78969]', swatchClass: 'bg-[#b78969]' })
  }

  return rows
    .filter((item) => String(item.rawPrice || '').trim())
    .map((item) => ({
      ...item,
      price: formatCurrency(item.rawPrice) || String(item.rawPrice || '').trim()
    }))
})

const venueRuleItems = computed(() => ([
  {
    icon: '🍽️',
    label: 'Catering',
    rows: [
      formatStatusPolicyRow(policies.value.inHouseCatering, 'In-house catering'),
      formatStatusPolicyRow(policies.value.outsideCatering, 'Outside catering')
    ].filter(Boolean),
    value: 'Catering policy not added yet.'
  },
  {
    icon: '🌸',
    label: 'Decoration',
    value: policies.value.decorPolicy || 'Decoration policy not added yet.'
  },
  {
    icon: '🎵',
    label: 'DJ / Music',
    value: policies.value.musicPolicy || 'Music policy not added yet.'
  },
  {
    icon: '🍸',
    label: 'Alcohol',
    rows: [
      formatStatusPolicyRow(policies.value.alcoholInhouse, 'In-house bar / alcohol available'),
      formatStatusPolicyRow(policies.value.alcoholOutside, 'Outside alcohol permitted')
    ].filter(Boolean),
    value: alcoholPolicySummary.value
  },
  {
    icon: '🔊',
    label: 'Noise curfew',
    value: policies.value.noiseMusic ? `Music must end by ${policies.value.noiseMusic}.` : 'Noise or music curfew not added yet.'
  },
  {
    icon: '📋',
    label: 'House rules',
    value: policies.value.otherHouseRules || 'No additional house rules added yet.'
  }
]))

const policyRuleItems = computed(() => venueRuleItems.value.filter((item) => item.label !== 'Catering'))
const cateringRestrictionRows = computed(() => venueRuleItems.value.find((item) => item.label === 'Catering')?.rows || [])

const bookingTermItems = computed(() => ([
  {
    icon: '👥',
    label: 'Guest count',
    value: policies.value.minGuests || policies.value.maxGuests
      ? `${policies.value.minGuests || 'Flexible'}-${policies.value.maxGuests || 'Flexible'} guests`
      : 'Guest count policy not added yet.'
  },
  {
    icon: '🔧',
    label: 'Setup time',
    value: policies.value.decorTime || 'Setup timing not added yet.'
  },
  {
    icon: '🧹',
    label: 'Cleanup time',
    value: policies.value.cleanTime || 'Cleanup timing not added yet.'
  },
  {
    icon: '💳',
    label: 'Deposit',
    value: policies.value.depositPercent ? `${policies.value.depositPercent}% of total to confirm booking` : 'Deposit terms not added yet.'
  },
  {
    icon: '↩️',
    label: 'Cancellation',
    value: policies.value.cancelTerms || 'Cancellation policy not added yet.'
  }
]))

const loadVenueGalleryPreview = async (venueId) => {
  const categoriesResponse = await api.getVenueGalleryCategories(new FormData())
  const categories = normalizeApiCollection(categoriesResponse?.data, ['categories', 'gallery_categories', 'items', 'list'])
  const tagIds = categories
    .map((item) => String(item?.id || item?.gallery_category_id || item?.tag_id || '').trim())
    .filter(Boolean)

  if (!tagIds.length) {
    const formData = new FormData()
    formData.append('venue_id', venueId)
    const galleryResponse = await api.getVenueGallery(formData)
    const payload = galleryResponse?.data?.data || galleryResponse?.data || {}
    const items = normalizeApiCollection(galleryResponse?.data, ['gallery', 'images', 'items', 'list'])
    return normalizeVenueGalleryEntries(items.length ? items : payload)
  }

  const responses = await Promise.all(tagIds.map(async (tagId) => {
    const formData = new FormData()
    formData.append('venue_id', venueId)
    formData.append('tag', tagId)
    const galleryResponse = await api.getVenueGallery(formData)
    const payload = galleryResponse?.data?.data || galleryResponse?.data || {}
    const items = normalizeApiCollection(galleryResponse?.data, ['gallery', 'images', 'items', 'list'])
    return normalizeVenueGalleryEntries(items.length ? items : payload)
  }))

  const seen = new Set()
  return responses.flat().filter((item) => {
    const key = `${item.id}-${item.src}`
    if (seen.has(key)) return false
    seen.add(key)
    return true
  })
}

const loadVenue = async () => {
  loading.value = true
  error.value = ''

  try {
    const businessFormData = new FormData()
    businessFormData.append('business_id', route.params.id)
    const venueFormData = new FormData()
    venueFormData.append('business_id', route.params.id)
    venueFormData.append('venue_id', route.params.venueId)
    const [businessResult, venueResult] = await Promise.allSettled([
      api.editBusinessVendor(businessFormData),
      api.listVenuesBusinessVendor(venueFormData)
    ])

    let hasPartialFailure = false

    if (businessResult.status === 'fulfilled') {
      const businessPayload = Array.isArray(businessResult.value?.data?.data)
        ? businessResult.value.data.data[0]
        : businessResult.value?.data?.data || businessResult.value?.data || {}
      businessName.value = businessPayload?.business_name || businessPayload?.businessName || businessPayload?.name || businessName.value
    } else {
      hasPartialFailure = true
    }

    if (venueResult.status === 'fulfilled') {
      const venuePayload = venueResult.value?.data?.data || venueResult.value?.data || {}
      const venueItems = Array.isArray(venuePayload)
        ? venuePayload
        : venuePayload?.venues || venuePayload?.items || venuePayload?.list || venuePayload?.venue_list || []
      const matchedVenue = Array.isArray(venueItems)
        ? venueItems.find((item) => String(item?.id || item?.venue_id || item?.vendor_business_venue_id || '') === String(route.params.venueId))
        : null
      venue.value = normalizeVenue(matchedVenue || venueItems[0] || {})
    } else {
      hasPartialFailure = true
      venue.value = normalizeVenue({
        id: route.params.venueId,
        title: route.query.venueTitle || 'Venue detail',
        address: route.query.venueAddress || '',
        status: 'Draft'
      })
    }

    const spacesFormData = new FormData()
    spacesFormData.append('venue_id', route.params.venueId)
    const packagesFormData = new FormData()
    packagesFormData.append('venue_id', route.params.venueId)
    const partnersFormData = new FormData()
    partnersFormData.append('venue_id', route.params.venueId)
    const policiesFormData = new FormData()
    policiesFormData.append('venue_id', route.params.venueId)

    const [spacesResult, packagesResult, partnersResult, policiesResult, galleryResult] = await Promise.allSettled([
      api.listVenueSpaces(spacesFormData),
      api.getPackagesVenue(packagesFormData),
      api.listVenueVendorPartners(partnersFormData),
      api.editPoliciesVenue(policiesFormData),
      loadVenueGalleryPreview(String(route.params.venueId))
    ])

    if (spacesResult.status === 'fulfilled') {
      const spaceData = spacesResult.value?.data?.data || spacesResult.value?.data || {}
      const items = Array.isArray(spaceData) ? spaceData : spaceData?.spaces || spaceData?.items || spaceData?.list || []
      spaces.value = items.map((item, index) => normalizeSpace(item, index))
      selectedSpaceId.value = spaces.value[0]?.id || ''
    } else {
      spaces.value = []
      selectedSpaceId.value = ''
    }

    if (packagesResult.status === 'fulfilled') {
      const packageData = packagesResult.value?.data?.data || packagesResult.value?.data || {}
      const items = Array.isArray(packageData) ? packageData : packageData?.packages || packageData?.items || packageData?.list || []
      packages.value = items.map((item, index) => normalizePackage(item, index))
    } else {
      packages.value = []
    }

    if (partnersResult.status === 'fulfilled') {
      const partnerData = partnersResult.value?.data?.data || partnersResult.value?.data || {}
      const items = Array.isArray(partnerData) ? partnerData : partnerData?.partners || partnerData?.vendor_partners || partnerData?.items || partnerData?.list || []
      partners.value = items.map((item, index) => normalizePartner(item, index))
    } else {
      partners.value = []
      hasPartialFailure = true
    }

    if (policiesResult.status === 'fulfilled') {
      const policyItem = normalizeApiCollection(policiesResult.value?.data, ['policies', 'policy', 'items', 'list'])[0] || policiesResult.value?.data?.data || policiesResult.value?.data || {}
      policies.value = {
        inHouseCatering: policyItem?.in_house_catering || '',
        outsideCatering: policyItem?.outside_catering || '',
        cateringFoodType: policyItem?.catering_food_type || policyItem?.food_type || policyItem?.veg_non_veg || '',
        cateringPriceUnit: policyItem?.catering_price_unit || policyItem?.price_unit || policyItem?.price_per || policyItem?.pricing_basis || '',
        pricingBasisCount: policyItem?.pricing_basis_count || policyItem?.count || '',
        nonVegPricingBasisCount: policyItem?.pricing_basis_count_nonveg || policyItem?.nonveg_count || '',
        decorPolicy: policyItem?.decor_policy || '',
        musicPolicy: policyItem?.music_policy || '',
        alcoholInhouse: policyItem?.alcohol_inhouse || '',
        alcoholOutside: policyItem?.alcohol_outside || '',
        minGuests: policyItem?.min_guests || '',
        maxGuests: policyItem?.max_guests || '',
        decorTime: policyItem?.decor_time || '',
        cleanTime: policyItem?.clean_time || '',
        noiseMusic: policyItem?.noise_music || '',
        otherHouseRules: policyItem?.other_house_rules || '',
        cancelTerms: policyItem?.cancel_terms || '',
        depositPercent: policyItem?.deposit_percent || ''
      }
    } else {
      hasPartialFailure = true
    }

    if (galleryResult.status === 'fulfilled') {
      gallery.value = galleryResult.value
    } else {
      gallery.value = []
      hasPartialFailure = true
    }

    if (hasPartialFailure) {
      error.value = 'Some venue details could not be loaded. Showing the preview with fallback content.'
    }
  } catch (err) {
    error.value = 'Some venue details could not be loaded. Showing the preview with fallback content.'
  } finally {
    loading.value = false
  }
}

const destroyGalleryLightbox = () => {
  if (galleryLightbox.value) {
    galleryLightbox.value.destroy(true)
    galleryLightbox.value = null
  }
}

const openVenueGallery = (startIndex = 0) => {
  if (!lightboxGalleryImages.value.length) return
  destroyGalleryLightbox()
  galleryLightbox.value = lightGallery(document.createElement('div'), {
    dynamic: true,
    dynamicEl: lightboxGalleryImages.value.map((image) => ({
      src: image.src,
      thumb: image.thumb,
      subHtml: image.tagName ? `<span class="text-sm font-semibold">${image.tagName}</span>` : ''
    })),
    index: Math.max(0, startIndex),
    plugins: [lgThumbnail, lgZoom],
    download: false,
    actualSize: false,
    mobileSettings: {
      controls: true,
      showCloseIcon: true,
      download: false
    }
  })
  galleryLightbox.value.openGallery(startIndex)
}

const openVenueGalleryById = (image) => {
  const imageId = String(image?.id || '')
  const startIndex = lightboxGalleryImages.value.findIndex((item) => String(item.id || '') === imageId)
  openVenueGallery(startIndex >= 0 ? startIndex : 0)
}

const goBack = () => {
  router.push({
    name: 'business-module-edit',
    params: {
      businessId: route.params.id,
      moduleType: 'venue',
      moduleId: route.params.venueId
    },
    query: {
      businessName: businessName.value,
      section: 'preview'
    }
  })
}

onMounted(() => {
  loadVenue()
})

onBeforeUnmount(() => {
  destroyGalleryLightbox()
})
</script>
